import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { session } from "../../lib/session";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Avatar, FriendChip, FriendPickerHint, Glyph, Modal, FormShell, FormFooter, ConfirmDelete, Chk, Timer, Stopwatch, DateField, useProfileModal } from "../../components/ui";
import { Button, Field, Input, Select, IconButton, SectionHeader, Caption, Tag, StateDot, DescrBox, List, ListRow, StreakTag, DifficultyPicker, DifficultyTag, Sheet, ProgressBar, ActionMenu } from "../../components/ui/ds";
import { normDifficulty, DIFF_SK } from "../../lib/points";
import { IconColorModal } from "../../components/forms/IconColorModal";
import { ReminderPrompt } from "../../components/forms/ReminderPrompt";
import { RecurrenceField, DEFAULT_RECUR, recurFromRow, recurToBody } from "../../components/forms/RecurrenceField";
import { PrevRoundField, nextRoundName } from "../../components/forms/PrevRoundField";
import { RoundSection } from "./RoundSection";
import { CompetitionStats } from "../buzer/MetricStats";
import "./competition-detail.css";
import { iconChipStyle } from "../../lib/icons-resolve";
import { Stat, FirstWeekNote } from "../buzer/TaskDetail";
import { LIFE_CATS, LIFE_CAT, START_MAX_AHEAD_DAYS } from "../../lib/constants";
import { Pill, Segmented } from "../../components/ui/ds";
import { Countdown } from "./Countdown";
import { todayKey, chatDayHeading, fmtDur, parseDur, shiftDay, streakLabel, freqLabel, pastVerb } from "../../lib/format";
import { fmtDuePretty } from "../../lib/date-sk";

// feat/challenge-visibility — zakladateľské ovládanie ÚČASTNÍKOV, zdieľané ChallengeBoard (výzva)
// aj CompetitionDetail (súťaž): čakajúce žiadosti o vstup do uzavretej VEREJNEJ výzvy/súťaže
// (schváliť → 'joined' / zamietnuť → riadok zmizne) — vidí ich len zakladateľ.
function FounderRequests({ cid, requests, onChanged }) {
  const [busy, setBusy] = useState(null);
  if (!requests || requests.length === 0) return null;
  async function act(uid, action) {
    setBusy(uid);
    try { await api(`/api/challenges/${cid}/requests/${uid}/${action}`, { method: "POST" }); onChanged(); }
    catch (e) { /* */ }
    setBusy(null);
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <SectionHeader label={`Žiadosti o vstup · ${requests.length}`} />
      {requests.map((r) => (
        <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 2px" }}>
          <Avatar user={r} size={28} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
          <Button variant="secondary" size="sm" disabled={busy === r.user_id} onClick={() => act(r.user_id, "approve")}>{busy === r.user_id ? "…" : "Schváliť"}</Button>
          <IconButton variant="ghost" size="sm" aria-label="Zamietnuť žiadosť" title="Zamietnuť" disabled={busy === r.user_id}
            onClick={() => act(r.user_id, "reject")} style={{ color: "var(--danger)" }}><Icon name="close" size={15} color="currentColor" /></IconButton>
        </div>
      ))}
    </div>
  );
}

// feat/challenge-visibility — správa účastníkov (len zakladateľ) ŽIJE VO FORMULÁRI „Upraviť".
// V rebríčku bolo × pri každom riadku a dalo sa ťuknúť omylom — odstránenie je nezvratné
// (vylúčenému sa skryje backing task), takže patrí za jeden krok navyše a s potvrdením.
function ParticipantsEdit({ cid, members, onChanged }) {
  const [ask, setAsk] = useState(null);   // {user_id, name} — potvrdenie odstránenia
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const others = (members || []).filter((m) => !m.is_me);
  if (others.length === 0) return null;
  async function kick() {
    setBusy(true); setErr("");
    try { await api(`/api/challenges/${cid}/members/${ask.user_id}`, { method: "DELETE" }); setAsk(null); onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }
  return (
    <div>
      <div style={st.fieldHint}>Účastníci:</div>
      <List>
        {others.map((m) => (
          <ListRow key={m.user_id} lead={<Avatar user={m} size={28} />} title={m.name}
            trailing={<IconButton variant="ghost" size="sm" aria-label={`Odstrániť ${m.name} z výzvy`}
              title="Odstrániť z výzvy" onClick={() => { setErr(""); setAsk(m); }}
              style={{ color: "var(--danger)" }}><Icon name="close" size={15} color="currentColor" /></IconButton>} />
        ))}
      </List>
      {err && <div style={{ ...st.err, marginTop: 8 }}>{err}</div>}
      {ask && (
        <Modal center title="Odstrániť účastníka?" icon="close" maxWidth={360} onClose={() => { if (!busy) setAsk(null); }}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>
            <b>{ask.name}</b> odíde z výzvy — jeho návyk sa mu skryje a do výzvy sa vráti až s novou
            pozvánkou (pri verejnej výzve novou žiadosťou). Doterajšie výsledky ostávajú.
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" block onClick={() => setAsk(null)} disabled={busy}>Zrušiť</Button>
            <Button variant="destructive" block disabled={busy} onClick={kick}>{busy ? "…" : "Odstrániť"}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

import { DUR_PRESETS, DUR_OPTIONS, SUGGEST_ICONS } from "../../lib/constants";

// Challenge/competition sub-system: leaderboard board, create/edit form, competition detail.
// Used by BuzerTab (features/buzer) and FriendsTab (features/social).

// v29 §10 — riadok mena v rebríčku: meno · „TY" · dnešná fajka · ŠTÍTOK SÉRIE.
// Rovnaký vzor sa vyskytuje v troch rebríčkoch (mini board výzvy, plný board výzvy,
// rebríček súťaže), preto je to jeden zdieľaný komponent, nie tri kópie (§6a pravidlá 5–7).
// Riadok je pri 360 px plný (poradie + avatar + meno + hodnota), takže sa ZALAMUJE
// (flexWrap) a samotné meno sa skracuje — štítok série nikdy nepretlačí kartu.
const BOARD_NAME_ROW = { ...st.boardName, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, minWidth: 0 };
const BOARD_NAME_TEXT = { minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
function BoardName({ name, isMe, doneToday, streak, unit, extended, lite }) {
  return (
    <div style={BOARD_NAME_ROW}>
      <span style={BOARD_NAME_TEXT}>{name}</span>
      {isMe ? <Tag tone="neutral" size="xs">TY</Tag> : null}
      {doneToday ? <StateDot state="done" size={13} /> : null}
      <StreakTag streak={streak} unit={unit} extended={extended} lite={lite} size="xs" />
    </div>
  );
}

// v26 §3.12 — popis výzvy/súťaže v zvýraznenom rámiku; DescrBox je teraz zdieľaný primitív (ds/DescrBox),
// aby detail návyku aj výzvy/súťaže používali rovnaký vzhľad.

// v26 §3.9 — vlastné pripomenutie výzvy per člen: číta/zapisuje remind_at MOJHO backing tasku
function ChallengeRemind({ taskId, value }) {
  const [ra, setRa] = useState(value || "");
  const save = async (next) => {
    setRa(next);
    try { await api("/api/tasks/" + taskId, { method: "PATCH", body: { remind_at: next || null } }); } catch (e) { /* */ }
  };
  const [hh, mm] = (ra || "08:00").split(":");
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  if (mm && !mins.includes(mm)) mins.push(mm);
  return (
    <div className="bz-remind">
      {/* v-tile — hlavička ako pilulka (.bz-tile): label vľavo, prepínač vpravo VNÚTRI pilulky */}
      <div className="bz-remind__pill">
        <span className="bz-tile__icon"><BuzerIcon name="bell" size={16} color="currentColor" /></span>
        <span className="bz-remind__label">Upozorniť ma v čase</span>
        <button type="button" role="switch" aria-checked={!!ra} aria-label="Zapnúť pripomenutie"
          onClick={() => save(ra ? "" : "08:00")}
          style={{ ...st.switch, ...(ra ? st.switchOn : {}), flex: "none" }}>
          <span style={{ ...st.switchKnob, ...(ra ? st.switchKnobOn : {}) }} />
        </button>
      </div>
      {!!ra && (
        <div className="bz-remind__times">
          <Select value={hh} style={{ width: 96, fontSize: 16 }} aria-label="Hodina" onChange={(e) => save(e.target.value + ":" + (mm || "00"))}>
            {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h} value={h}>{h}</option>)}
          </Select>
          <span className="bz-remind__sep">:</span>
          <Select value={mm} style={{ width: 96, fontSize: 16 }} aria-label="Minúta" onChange={(e) => save((hh || "08") + ":" + e.target.value)}>
            {mins.sort().map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </div>
      )}
      {!!ra && <div style={{ ...st.fieldHint, marginTop: 6 }}>Príde len tebe — každý člen si nastavuje vlastný čas.</div>}
    </div>
  );
}

// v33 — zaradenie do SKUPINY priamo z detailu. Doteraz sa dalo len ťahaním na Dnes, čo ľudia
// nenašli (hlásenie majiteľky 2026-09-03). Drag & drop ostáva, toto je druhá, viditeľná cesta.
// Jeden riadok so `Select`om — zámerne bez vlastného štýlu, nech vyzerá ako zvyšok formulárov.
// Bez jedinej skupiny sa nevykreslí nič (nebolo by z čoho vyberať).
function GroupPicker({ categories, value, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!categories || !categories.length) return null;
  return (
    <div>
      <Select value={value == null ? "" : String(value)} disabled={busy}
        onChange={async (e) => {
          const v = e.target.value ? Number(e.target.value) : null;
          setBusy(true);
          try { await onChange(v); } finally { setBusy(false); }
        }}>
        <option value="">Bez skupiny</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </Select>
    </div>
  );
}

export function ChallengeBoard({ cid, onChanged, reload, inModal, onRequestUncheck, lite = false, refreshKey = 0 }) {
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);  // feat/globalne-pozvanky — sheet „Pozvať"
  const [delAsk, setDelAsk] = useState(false);  // v31 — „Zmazať výzvu?" potvrdenie s „Radšej archivovať"
  // v29 §9.2 — optimisticky zvolená MOJA náročnosť (kým sa detail znovu nenačíta), nech výber
  // neposkakuje späť na starú hodnotu počas PATCH-u
  const [myDiff, setMyDiff] = useState(null);
  // §7.4 — in the habit modal show a compact mini board (top 3 + me); elsewhere the full list
  const load = useCallback(async () => { setData(await api("/api/challenges/" + cid + (inModal ? "?board=mini" : ""))); }, [cid, inModal]);
  useEffect(() => { load(); }, [load]);
  // ITEM 3 — rodič bumpne refreshKey po zrušení splnenia (uncheck cez toggle), aby sa doneToday
  // v tomto boarde znovu načítalo a tlačidlo sa vrátilo na „Splniť dnes"
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div style={{ display: "flex", justifyContent: "center", padding: 16 }}><div className="spin" /></div>;
  if (editing) return (
    <div style={inModal ? {} : st.boardCard}>
      <ChallengeCreate
        challenge={{ id: cid, name: data.name, icon: data.icon, icon_color: data.icon_color, desc: data.descr, points: data.points,
          timer_sec: data.timer_sec, timer_min: data.timer_min,  // fix — inak sa uložený časovač v Upraviť nepredvyplní
          // feat/vyzva-frekvencia — celá rekurencia (rovnaké kľúče ako pri návyku) do formulára
          recur_mode: data.recur_mode, recur_n: data.recur_n, recur_days: data.recur_days,
          recur_period: data.recur_period, per_day: data.per_day,
          duration_days: data.duration_days, category: data.category,
          visibility: data.visibility, is_public: data.is_public, is_official: data.is_official,
        prev_id: data.prev && data.round_no > 1 ? data.prev.id : null }}  // feat/challenge-visibility + v33 kolá
        memberIds={data.members.map((m) => m.user_id)}
        members={data.members} isCreator={data.is_creator}
        onAdded={() => { load(); reload && reload(); }}
        onDone={() => setEditing(false)} />
    </div>
  );

  async function leave() { if (!confirm("Opustiť výzvu? Tvoj návyk sa skryje a v archíve sa neuloží.")) return; await api("/api/challenges/" + cid + "/leave", { method: "DELETE" }); onChanged(); }
  async function del() { if (!confirm("Zmazať výzvu pre všetkých účastníkov?")) return; await api("/api/challenges/" + cid, { method: "DELETE" }); onChanged(); }
  // v26 — archivácia: tvorca ukončí k dnešku pre všetkých; člen odíde, ale ostane mu v archíve
  async function archiveAll() { if (!confirm("Ukončiť výzvu k dnešku pre všetkých členov? Presunie sa do archívu s výsledkami.")) return; await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "all" } }); onChanged(); }
  async function archiveMe() { if (!confirm("Archivovať výzvu u teba? Odídeš z nej, ale výsledky ti ostanú v archíve.")) return; await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "me" } }); onChanged(); }
  // v17 — complete (toggle) today's challenge habit straight from the výzva modal
  async function completeToday() {
    const meRow = data && (data.members || []).find((m) => m.is_me);
    const tid = (meRow && meRow.task_id) || (data && data.me && data.me.task_id);
    if (!tid) return;
    try { await api("/api/toggle", { method: "POST", body: { task_id: tid, date: todayKey() } }); load(); reload && reload(); } catch (e) { /* */ }
  }
  // v29 §9.2 — „Tvoja náročnosť": mení sa LEN môj podkladový návyk (challenge_members.task_id),
  // takže si každý člen môže zvoliť vlastnú náročnosť bez dopadu na ostatných. Rebríček výzvy
  // sa radí podľa počtu splnení, nie bodov, takže férovosť poradia to nenaruší.
  const ctrlRow = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
      {data.is_creator
        ? <button onClick={() => setEditing(true)} style={{ ...st.linkBtn, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="edit" size={14} color="currentColor" /> Upraviť výzvu</button>
        : <span />}
      {data.is_creator
        ? <span style={{ display: "inline-flex", gap: 14 }}>
            <button onClick={archiveAll} style={st.linkBtn}>Archivovať</button>
            <button onClick={del} style={st.linkDanger}>Zmazať výzvu</button>
          </span>
        : <span style={{ display: "inline-flex", gap: 14 }}>
            <button onClick={archiveMe} style={st.linkBtn}>Archivovať u mňa</button>
            <button onClick={leave} style={st.linkDanger}>Opustiť výzvu</button>
          </span>}
    </div>
  );

  // §7.4 / v17 — výzva mini board (mock #5): desc+points, rebríček top-3 + me, Tvoje miesto, Splniť dnes / Opustiť
  // TODO(v29-open): štítok náročnosti výzvy POD ikonou (§7.2) sa musí doplniť v hlavičke `Sheet`
  // (`iconBelow`), ktorá žije v BuzerFeature.jsx — mimo súborov tohto agenta. Viď v29-open-questions.
  if (data.mini) {
    const leader = data.leader_points || 1;
    const meRow = (data.members || []).find((m) => m.is_me) || data.me;
    const meShown = (data.members || []).some((m) => m.is_me);
    // v22 — 7-dňový prúžok pre viditeľných účastníkov (ktoré dni splnili výzvu)
    const last7v = [];
    for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); last7v.push(d.toISOString().slice(0, 10)); }
    const dayStrip = (hist) => (
      <div style={{ display: "flex", gap: 3, marginTop: 7 }}>
        {last7v.map((k) => <span key={k} title={k} style={{ flex: 1, height: 6, borderRadius: 3, background: (hist && hist[k] > 0) ? "var(--accent)" : "var(--accent-tint)" }} />)}
      </div>
    );
    const rankBadge = (rank) => (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 12.5,
        ...(rank === 1 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{rank}</span>
    );
    // v19 — each row is a padded, bordered card (mock #17): side padding, smaller rank/value
    const bar = (m) => (
      <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "14px 14px", borderRadius: 16, marginBottom: 10, border: "1px solid " + (m.is_me ? "var(--accent-tint)" : "var(--border)"), background: m.is_me ? "var(--ember-soft)" : "var(--surface-solid)" }}>
        {rankBadge(m.rank)}
        <Avatar user={m} size={30} onClick={() => openPublicProfile(m.user_id)} />
        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openPublicProfile(m.user_id)}>
          <BoardName name={m.name} isMe={m.is_me} doneToday={m.doneToday}
            streak={m.streak} unit={m.streak_unit} extended={m.streak_extended_today} lite={lite} />
          <div style={{ height: 6, borderRadius: 99, background: "var(--accent-tint)", marginTop: 5, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(4, (m.totalPoints / leader) * 100)}%`, background: "var(--accent-grad)" }} />
          </div>
          {m.history ? dayStrip(m.history) : null}
        </div>
        <span style={{ ...st.taskDesc, flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 12.5, alignSelf: "flex-start" }}>{m.totalPoints}×</span>
      </div>
    );
    return (
      <div style={inModal ? {} : st.boardCard}>
        {/* Bug#2 — náznak, že opt-in skupinový chat výzvy už žije (má správy): jemná pomôcka
            vedľa ikony chatu v hlavičke sheetu, nech účastník vie, že sa oplatí pridať. */}
        {data.chat_has_messages && (
          <Caption icon="chat" style={{ marginBottom: 10 }}>V chate sú už správy</Caption>
        )}
        {/* v26 §3.12 — popis v rámiku NAD dlaždicami */}
        <DescrBox text={data.descr} style={{ marginBottom: 16 }} />
        {/* v2 — same Stat tiles as the návyk detail (body · frekv. · séria · účastník) */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          {/* v29 §7.2 — náročnosť NAMIESTO bodovej dlaždice (rovnako ako v detaile návyku).
              Ukazuje MOJU náročnosť, lebo tá určuje body, ktoré za výzvu naozaj dostanem. */}
          {!lite && <DifficultyTag level={myDiff || data.my_difficulty || data.difficulty} streak={(meRow && meRow.streak) || 0} info />}
          {/* feat/vyzva-frekvencia — štítok frekvencie počíta ten istý `freqLabel` ako detail
              návyku, takže vie aj „3× mesiac", „každý 3. deň" či „2× denne" */}
          <Stat icon={<BuzerIcon name="calendar" size={17} color="currentColor" />} value={freqLabel(data)} label="FREKV." />
          {/* v33h — Lite (bez bodov) má sériu vo farbe témy, nie streak-oranžovú */}
          {/* v29 §3.3 — séria má už len dve jednotky ('day' → „5 dní", 'times' → „5×") a skloňuje
              ich zdieľaný streakLabel; pôvodné ad-hoc „týždeň/týždne/týždňov" je zvyšok starého modelu */}
          <Stat icon={<BuzerIcon name="flame" size={17} color={lite ? "var(--brand-primary)" : "var(--streak)"} />}
            value={streakLabel((meRow && meRow.streak) || 0, meRow && meRow.streak_unit)}
            valueColor={lite ? "var(--brand-primary)" : "var(--streak)"} label="SÉRIA" />
          <Stat icon={<BuzerIcon name="people" size={17} color="currentColor" />} value={data.total} label="ÚČASTNÍCI" />
          {/* v32/v33 — životná kategória + koľko zostáva do konca výzvy (kategória z jednotného
              `life_cat`, fallback na `category`) — v jednom balenom riadku spolu s ostatnými pilulkami */}
          {LIFE_CAT[data.life_cat || data.category] && (
            <Stat icon={<BuzerIcon name={LIFE_CAT[data.life_cat || data.category].icon} size={17} color="currentColor" />} value={LIFE_CAT[data.life_cat || data.category].label} label="KATEGÓRIA" />
          )}
          {data.days_left != null && (
            <Stat icon={<BuzerIcon name="hourglass" size={17} color="currentColor" />} value={`${data.days_left} ${data.days_left === 1 ? "deň" : (data.days_left >= 2 && data.days_left <= 4 ? "dni" : "dní")}`} label="DO KONCA" />
          )}
          {/* v34 — pripomienková pilulka je súčasťou skupiny piluliek (neodskakuje na nový riadok) */}
          {/* dosiahnutá frekvencia = ROVNAKÁ pilulka (bz-tile) ako v detaile návyku: bodky sú „ikona",
              N/N je hodnota, „splnené" label. Cieľ (week_target) je pomerový v prvom neúplnom týždni
              (Plán A) → zhoduje sa s miniatúrou na Dnes; plný cieľ vysvetľuje bublina pod dlaždicami. */}
          {data.week_full > 0 && (
            <Stat
              icon={<span style={{ display: "inline-flex", gap: 4 }}>
                {Array.from({ length: data.week_target || 0 }).map((_, i) => (
                  <span key={i} style={{ width: 9, height: 9, borderRadius: "50%", background: i < (data.week_done || 0) ? "var(--brand-primary)" : "transparent", border: i < (data.week_done || 0) ? "none" : "1.5px solid var(--neutral-border-strong)" }} />
                ))}
              </span>}
              value={`${data.week_done || 0}/${data.week_target}`}
              label="splnené"
            />
          )}
        </div>
        {/* v35 — zdieľaná „prvý týždeň" bublina (rovnaká ako v detaile návyku): vysvetlí znížený
            cieľ prvého neúplného týždňa. Bez nej pôsobí „2×" pri nastavenej 4×/týždeň ako chyba. */}
        {/* text bubliny hovorí o TÝŽDNI → ukazuj ju len pri týždennej frekvencii (weekly_count);
            pri „N× za mesiac/pol rok/rok" by bol jej text nepravdivý */}
        {data.recur_mode === "weekly_count" && <FirstWeekNote target={data.week_target || 0} full={data.week_full || 0} style={{ marginTop: 0, marginBottom: 16 }} />}
        {/* v33 §6.3 — KOLÁ: pri výzve sa porovnáva percento splnenia, nie hodnota */}
        {data.round_no > 1 && data.prev && <RoundSection data={data} fmtVal={(v) => Math.round(v) + " %"} />}
        {/* v29 §9.2 — „Tvoja náročnosť": zakladateľ určí náročnosť výzvy, ale každý člen si ju
            môže pre SEBA prestaviť (mení len jeho body, nie poradie v rebríčku). Lite nemá body,
            takže tam sa výber nezobrazuje vôbec (§8.3). */}
        {/* feat/challenge-visibility — čakajúce žiadosti o vstup (len zakladateľ, uzavretá verejná) */}
        {data.is_creator && <FounderRequests cid={cid} requests={data.pending_requests} onChanged={load} />}
        {/* feat/globalne-pozvanky — pozvať do GLOBÁLNEJ výzvy smie ktorýkoľvek účastník
            (zakladateľ má pozvánky vo „Upraviť výzvu", tam by tlačidlo bolo duplicita).
            Sedí pri rebríčku: pozvánka = ďalší účastník. */}
        <SectionHeader label="Rebríček" style={{ margin: "2px 2px 12px" }}
          count={`${data.total} ${data.total === 1 ? "účastník" : (data.total >= 2 && data.total <= 4 ? "účastníci" : "účastníkov")}`}
          trailing={data.can_invite && !data.is_creator ? (
            <Button variant="ghost" size="sm" onClick={() => setInviting(true)}>
              <BuzerIcon name="people" size={15} color="currentColor" />Pozvať
            </Button>
          ) : null} />
        {data.members.map(bar)}
        {!meShown && data.me && (<>
          <div style={{ textAlign: "center", color: "var(--muted)", letterSpacing: 2, margin: "-2px 0 2px" }}>···</div>
          {bar(data.me)}
        </>)}
        {data.my_rank ? <div style={{ ...st.taskDesc, textAlign: "center", marginTop: 14 }}>Tvoje miesto: <strong style={{ color: "var(--text)" }}>{data.my_rank}.</strong> z {data.total}</div> : null}
        {/* v2 — footer like the návyk detail: Splniť dnes full-width, Upraviť (or Opustiť) below.
            v33c — v modáli = sticky pätička (bz-sheet-stickyfoot), nech tlačidlá neodídu pod rebríčkom. */}
        <div className={inModal ? "bz-sheet-stickyfoot" : undefined}
          style={inModal ? undefined : { display: "flex", flexDirection: "column", gap: 9, marginTop: 18 }}>
          <Button variant="primary" block onClick={() => ((meRow && meRow.doneToday) ? (onRequestUncheck ? onRequestUncheck() : completeToday()) : completeToday())}>
            {meRow && meRow.doneToday ? <><Icon name="check" size={19} color="currentColor" stroke={2.6} />Splnené dnes</> : <><Icon name="check" size={19} color="currentColor" stroke={2.6} />Splniť dnes</>}
          </Button>
          {/* v2 — časované výzvy (napr. meditácia/čítanie): odpočet, po doznení sa dnešok sám splní.
              variant="button" — primárne tlačidlo „Spustiť časovač" ROVNAKO ako v detaile návyku
              (TaskDetail); po spustení sa prepne na sekundárne. onComplete ostáva (auto-splnenie). */}
          {data.timer_sec > 0 && !(meRow && meRow.doneToday) && (
            <Timer variant="button" id={"chal" + cid} seconds={data.timer_sec} onComplete={completeToday} />
          )}
          {data.is_creator
            ? <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" block onClick={() => setEditing(true)}><Icon name="edit" size={16} color="currentColor" />Upraviť</Button>
                <IconButton variant="ghost" aria-label="Zmazať výzvu" title="Zmazať výzvu" onClick={() => setDelAsk(true)} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
              </div>
            : <div style={{ display: "flex", gap: 8 }}>
                <Button variant="ghost" block onClick={archiveMe} title="Odísť, ale nechať si výsledky v archíve"><BuzerIcon name="folder" size={16} color="currentColor" />Archivovať u mňa</Button>
                <Button variant="ghost" block onClick={leave} style={{ color: "var(--danger)" }}><Icon name="logout" size={16} color="currentColor" />Opustiť</Button>
              </div>}
        </div>
        {inviting && (
          <ChallengeInviteSheet cid={cid} name={data.name} isComp={data.is_competition}
            memberIds={(data.members || []).map((m) => m.user_id)}
            onClose={() => setInviting(false)} onInvited={load} />
        )}
        {/* v31 — „Zmazať výzvu?" cez zdieľané ConfirmDelete (archív = primary, jedno miesto v kóde) */}
        {delAsk && (
          <ConfirmDelete title="Zmazať výzvu?" icon="trash"
            message={<>Archivovaním sa výzva ukončí k dnešku a <strong>všetkým ostane v archíve</strong>. Zmazaním zmizne u teba — ostatným účastníkom sa <strong>automaticky archivuje</strong> s výsledkami.</>}
            archiveLabel="Archivovať pre všetkých" deleteLabel="Zmazať u mňa"
            onArchive={async () => { setDelAsk(false); await api("/api/challenges/" + cid + "/archive", { method: "POST", body: { scope: "all" } }); onChanged(); }}
            onDelete={async () => { setDelAsk(false); await api("/api/challenges/" + cid, { method: "DELETE" }); onChanged(); }}
            onClose={() => setDelAsk(false)} />
        )}
      </div>
    );
  }

  const last7 = [];
  for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); last7.push(d.toISOString().slice(0, 10)); }
  let maxPts = 1;
  for (const m of data.members) for (const k of last7) maxPts = Math.max(maxPts, m.history[k] || 0);

  return (
    <div style={inModal ? {} : st.boardCard}>
      <div style={st.boardHead}>
        {inModal ? <span /> : <span style={st.boardTitle}>{data.icon} {data.name} · rebríček</span>}
        {data.invited_count > 0 && <span style={{ ...st.taskDesc, display: "inline-flex", alignItems: "center", gap: 5 }}><BuzerIcon name="hourglass" size={13} color="currentColor" />{data.invited_count} pozvaných</span>}
      </div>
      <DescrBox text={data.descr} style={{ marginBottom: 10 }} />
      {data.members.map((m, idx) => (
        <div key={m.user_id} style={{ ...st.boardRow, ...(m.is_me ? { background: "var(--ember-soft)", borderRadius: 12 } : {}) }}>
          {/* v18 §9 — unified rank badge */}
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 9, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 13,
            ...(idx === 0 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{idx + 1}</span>
          <Avatar user={m} size={28} onClick={() => openPublicProfile(m.user_id)} />
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => openPublicProfile(m.user_id)}>
            {/* v29 §10 — séria sa presunula k menu (farebná = dnes už predĺžená); v meta riadku
                ostáva už len počet splnení */}
            <BoardName name={m.name} isMe={m.is_me} doneToday={m.doneToday}
              streak={m.streak} unit={m.streak_unit} extended={m.streak_extended_today} lite={lite} />
            <div style={st.taskDesc}>{m.totalPoints}× splnené</div>
          </div>
          <div style={st.miniChart}>
            {last7.map((k) => {
              const p = m.history[k] || 0;
              return (
                <div key={k} style={st.miniTrack}>
                  <div style={{ ...st.miniBar, height: `${Math.max(8, (p / maxPts) * 100)}%`,
                    background: p > 0 ? "var(--accent-grad)" : "var(--accent-tint)" }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {ctrlRow}
    </div>
  );
}

// feat/vyzva-email-invite — zrkadlí validáciu a strop v app.py (`_valid_email`, `_INVITE_EMAIL_MAX`)
const EMAIL_RE = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;
const MAX_INVITE_EMAILS = 10;

// ── pozývanie do výzvy/súťaže (priatelia + e-mail) ─────────────────────────────────────
// feat/globalne-pozvanky — stav aj polia formulára žijú TU, nie v ChallengeCreate, lebo
// pozývať sa dá z dvoch miest: z „Vytvoriť/Upraviť výzvu" (zakladateľ) a zo sheetu
// „Pozvať" v detaile GLOBÁLNEJ výzvy (ktorýkoľvek účastník). Jedna mašinéria, dve dvere.
function useChallengeInvite({ open = true, memberIds, initialInvite } = {}) {
  const [friends, setFriends] = useState([]);
  const [invite, setInvite] = useState(initialInvite || []);
  const [friendQuery, setFriendQuery] = useState("");
  const [inviteEmails, setInviteEmails] = useState([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailErr, setEmailErr] = useState("");
  useEffect(() => { if (open) api("/api/friends").then((d) => setFriends(d.friends || [])).catch(() => {}); }, [open]);
  const invitable = friends.filter((f) => !(memberIds || []).includes(f.friend_id));
  function toggle(id) { setInvite((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); }
  function addEmail() {
    const em = emailDraft.trim().toLowerCase();
    if (!em) return;
    if (!EMAIL_RE.test(em)) { setEmailErr("Zadaj platný e-mail."); return; }
    if (inviteEmails.includes(em)) { setEmailDraft(""); return; }
    if (inviteEmails.length >= MAX_INVITE_EMAILS) { setEmailErr(`Naraz sa dá pozvať najviac ${MAX_INVITE_EMAILS} adries.`); return; }
    setInviteEmails((p) => [...p, em]); setEmailDraft(""); setEmailErr("");
  }
  // adresa, ktorú má človek ešte rozpísanú v poli, sa nesmie stratiť pri odoslaní
  function allEmails() {
    const pending = emailDraft.trim().toLowerCase();
    return (pending && EMAIL_RE.test(pending) && !inviteEmails.includes(pending))
      ? [...inviteEmails, pending] : inviteEmails;
  }
  function reset({ keepFriends = false } = {}) {
    setInviteEmails([]); setEmailDraft(""); setEmailErr("");
    if (!keepFriends) setInvite([]);
  }
  return { invitable, invite, toggle, friendQuery, setFriendQuery, inviteEmails, setInviteEmails,
    emailDraft, setEmailDraft, emailErr, setEmailErr, addEmail, allEmails, reset };
}

// Polia „Pozvať priateľov" + „Pozvať e-mailom". `emptyHint` prispôsobí hlášku, keď už
// nie je koho pozvať (formulár vs. detail výzvy).
function ChallengeInviteFields({ inv, editing = false, friendsLabel, emptyHint }) {
  return (<>
    <div>
      <div style={st.fieldHint}>{friendsLabel || (editing ? "Pozvať ďalších priateľov:" : "Pozvať priateľov:")}</div>
      {inv.invitable.length === 0 ? (
        <div style={st.taskDesc}>{emptyHint || (editing ? "Všetci priatelia sú už vo výzve." : "Zatiaľ žiadni priatelia — pridaj si ich v záložke Priatelia.")}</div>
      ) : (
        <>
          {/* G5 — search within the picker once there are many friends */}
          {inv.invitable.length > 6 && (
            <div style={{ position: "relative", marginBottom: 8 }}>
              <BuzerIcon name="search" size={16} color="var(--neutral-text-subtle)" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <Input style={{ paddingLeft: 38 }} placeholder="Hľadať priateľa…"
                value={inv.friendQuery} onChange={(e) => inv.setFriendQuery(e.target.value)} />
            </div>
          )}
          <FriendPickerHint />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 200, overflowY: "auto" }}>
            {inv.invitable.filter((f) => f.name.toLowerCase().includes(inv.friendQuery.trim().toLowerCase())).map((f) => (
              <FriendChip key={f.friend_id} friend={f} selected={inv.invite.includes(f.friend_id)} onToggle={inv.toggle}
                chipStyle={{ borderRadius: "var(--radius-pill)", padding: "6px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                  background: "var(--neutral-surface-2)", color: "var(--neutral-text-main)", border: "1.5px solid var(--neutral-border-strong)" }}
                chipOnStyle={{ background: "var(--brand-primary-surface)", color: "var(--brand-primary)", border: "1.5px solid var(--brand-primary-border)" }} />
            ))}
          </div>
        </>
      )}
    </div>
    {/* feat/vyzva-email-invite — pozvať aj niekoho, kto v appke ešte nie je: zadaj adresu,
        odíde mu slovenská pozvánka a po registrácii tým istým e-mailom ho výzva čaká medzi
        pozvánkami (nezapojí sa sám — návyk si nikto nepýtal). */}
    <div>
      <div style={st.fieldHint}>Pozvať e-mailom:</div>
      <div className="bz-mailinvite-row">
        <Input type="email" inputMode="email" autoComplete="off" placeholder="meno@email.sk"
          aria-label="E-mail pozvaného" value={inv.emailDraft}
          onChange={(e) => { inv.setEmailDraft(e.target.value); inv.setEmailErr(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); inv.addEmail(); } }} />
        <Button type="button" variant="secondary" onClick={inv.addEmail}>Pridať</Button>
      </div>
      {inv.emailErr && <div style={{ ...st.err, marginTop: 8 }}>{inv.emailErr}</div>}
      {inv.inviteEmails.length > 0 && (
        <div className="bz-mailinvite-list">
          {inv.inviteEmails.map((em) => (
            <Tag key={em} tone="neutral" className="bz-mailinvite-chip">
              <span className="bz-mailinvite-mail">{em}</span>
              <button type="button" className="bz-mailinvite-x" title="Odobrať"
                aria-label={`Odobrať ${em}`}
                onClick={() => inv.setInviteEmails((p) => p.filter((x) => x !== em))}>
                <Icon name="close" size={12} color="currentColor" />
              </button>
            </Tag>
          ))}
        </div>
      )}
      <Caption variant="note" style={{ marginTop: 8 }}>
        Ak appku ešte nemá, príde mu pozvánka na e-mail. Po registrácii ho výzva čaká medzi
        pozvánkami — stačí ťuknúť na „Zapojiť sa“.
      </Caption>
    </div>
  </>);
}

// feat/globalne-pozvanky — sheet „Pozvať do výzvy" pre GLOBÁLNU (oficiálnu) výzvu: tú
// nezaložil človek, takže do nej smie pozývať ktorýkoľvek zapojený účastník. Posiela na
// POST /api/challenges/<id>/invite (tá istá mašinéria ako pozvánky zo „Upraviť výzvu").
export function ChallengeInviteSheet({ cid, name, isComp, memberIds, onClose, onInvited }) {
  // práve pozvaní ostanú v zozname členov, aby sa nedali pozvať druhýkrát (server by ich
  // aj tak odmietol ako „already", ale ponuka by klamala)
  const [justInvited, setJustInvited] = useState([]);
  const inv = useChallengeInvite({ memberIds: [...(memberIds || []), ...justInvited] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(0);
  async function send() {
    const emails = inv.allEmails();
    if (!inv.invite.length && !emails.length) { setErr("Vyber priateľa alebo zadaj e-mail."); return; }
    setErr(""); setBusy(true);
    try {
      const picked = inv.invite;
      const r = await api("/api/challenges/" + cid + "/invite", { method: "POST", body: { friends: picked, emails } });
      inv.reset();
      setJustInvited((p) => [...p, ...picked]);
      setDone((d) => d + (r && r.friends != null ? r.friends : picked.length) + emails.length);
      onInvited && onInvited();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  return (
    <Sheet title={isComp ? "Pozvať do súťaže" : "Pozvať do výzvy"} icon="people" onClose={onClose}
      footer={<div className="bz-modal-actions" style={{ flexDirection: "column" }}>
        <Button variant="primary" block disabled={busy} onClick={send}>{busy ? "Posielam…" : "Odoslať pozvánky"}</Button>
        <Button variant="ghost" block onClick={onClose}>Zavrieť</Button>
      </div>}>
      <Caption variant="note" style={{ marginBottom: 14 }}>
        „{name}“ je spoločná {isComp ? "súťaž" : "výzva"} Buzerlístka — zapojiť sa môže ktokoľvek,
        tak pokojne pozvi, koho chceš. {isComp ? "Čím viac nás súťaží, tým je to zaujímavejšie."
          : "Čím viac nás je, tým lepšie sa to ťahá."}
      </Caption>
      {done > 0 && <div style={{ ...st.infoNote, marginBottom: 14 }}>Pozvánky odišli ({done}).</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <ChallengeInviteFields inv={inv} friendsLabel="Pozvať priateľov:"
          emptyHint="Všetci tvoji priatelia sú už vo výzve — pozvi niekoho e-mailom." />
      </div>
      {err && <div style={{ ...st.err, marginTop: 12 }}>{err}</div>}
    </Sheet>
  );
}

export function ChallengeCreate({ onAdded, challenge, memberIds, members, isCreator, onDone, mode, open: openProp, onOpenChange, initialInvite, preset }) {
  const editing = !!challenge;
  // v33 §4.1 — „Ďalšie kolo": formulár TVORBY predvyplnený z dobehnutého kola. Zámerne to nie je
  // režim úpravy — vzniká nová výzva, len s prevzatými nastaveniami a s `prev_id` na predchodcu.
  const pre = editing ? null : (preset || null);
  // v26.2 §6 — jeden typ Výzva; „Zapisovanie" volí spôsob (Splnenie=výzva / Vlastný výsledok=súťaž).
  const [isComp, setIsComp] = useState(editing ? !!challenge.is_competition : (pre ? !!pre.is_competition : mode === "competition"));
  // controlled open (single-open coordination); drafts kept in local state when collapsed.
  const controlled = openProp !== undefined;
  const [openState, setOpenState] = useState(editing);
  const open = controlled ? openProp : openState;
  const setOpen = (v) => { if (controlled) { onOpenChange && onOpenChange(v); } else setOpenState(v); };
  const [icon, setIcon] = useState(editing ? (challenge.icon || (isComp ? "trophy" : "flag")) : ((pre && pre.icon) || (isComp ? "trophy" : "flag")));
  const [iconColor, setIconColor] = useState(editing ? (challenge.icon_color || "") : ((pre && pre.icon_color) || ""));  // v26 §3.14
  const [iconPick, setIconPick] = useState(false);  // v8 BuzerIcon picker
  const [name, setName] = useState(editing ? challenge.name : (pre ? nextRoundName(pre.name) : ""));
  const [desc, setDesc] = useState(editing ? (challenge.desc || "") : ((pre && pre.desc) || ""));
  const [pts, setPts] = useState(editing ? challenge.points : 15);
  // v29 §9.1 — náročnosť výzvy (súťaž ju nemá, §9.3.5)
  const [difficulty, setDifficulty] = useState(editing ? normDifficulty(challenge.difficulty) : "normal");
  // feat/vyzva-frekvencia — frekvencia výzvy = tá istá rekurencia ako pri návyku (zdieľaný
  // `RecurrenceField`). Staré výzvy majú v payloade dopočítané recur_* z freq_per_week/freq_days,
  // takže sa v editácii predvyplní presne to, čo výzva doteraz robila.
  const [recur, setRecur] = useState(editing ? recurFromRow(challenge) : (pre ? recurFromRow(pre) : { ...DEFAULT_RECUR }));
  const [prevId, setPrevId] = useState(editing ? (challenge.prev_id || null) : ((pre && pre.id) || null));
  const recurChanged = editing && JSON.stringify(recur) !== JSON.stringify(recurFromRow(challenge));
  // v37 — nová výzva je predvolene BEZ LIMITU (rozhodnutie majiteľky). Týždňový default
  // tichým spôsobom rozhodoval za človeka a bil sa s frekvenciou typu „2× za mesiac", ktorá
  // sa v sedemdňovej výzve nedá stihnúť. 0 = bez limitu (backend: _clean_duration → NULL).
  const [duration, setDuration] = useState(editing ? (challenge.duration_days || 0) : 0);
  const [durCustom, setDurCustom] = useState(!!(editing && challenge.duration_days && !DUR_PRESETS.includes(challenge.duration_days)));
  const [metricLabel, setMetricLabel] = useState(editing ? (challenge.metric_label || "") : ((pre && pre.metric_label) || ""));
  const [metricDir, setMetricDir] = useState(editing ? (challenge.metric_dir || "high") : ((pre && pre.metric_dir) || "high"));
  const [resultTimer, setResultTimer] = useState(editing ? !!challenge.result_timer : !!(pre && pre.result_timer));  // v2 — stopky merajú výsledok
  const [multiDay, setMultiDay] = useState(editing ? !!challenge.multi_day : !!(pre && pre.multi_day));  // v2 — viac zápisov za deň (sčítavajú sa)
  const [evalDate, setEvalDate] = useState(editing ? (challenge.eval_date || "") : "");
  // ZAČIATOK behu (predvolene dnes). `created_at` výzvy = jej štart, takže z neho ide odpočet
  // „DO ŠTARTU" aj koniec (created_at + trvanie). Len pri TVORBE — posun začiatku bežiacej
  // výzvy by prepísal štart aj koniec pod rukami účastníkom, ktorí sa už zapojili.
  const [startDate, setStartDate] = useState(todayKey());
  const _initT = editing ? (challenge.timer_sec || (challenge.timer_min || 0) * 60) : 0;
  const [timerMin, setTimerMin] = useState(Math.floor(_initT / 60));
  const [timerSec, setTimerSec] = useState(_initT % 60);
  // v23 — preselect a friend (from their profile); feat/globalne-pozvanky — spoločný stav
  // pozývania (priatelia + e-mailové adresy) žije v `useChallengeInvite`
  const inv = useChallengeInvite({ open, memberIds, initialInvite });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [category, setCategory] = useState(editing ? (challenge.category || "") : ((pre && pre.category) || ""));  // v32 — životná kategória
  // feat/challenge-visibility — typ: súkromná (default, len pozvaní) / verejná (uzavretá — vidno ju
  // v „Prebiehajúcich výzvach" a na profiloch, vstup na žiadosť so schválením). Globálnu/oficiálnu (is_public/
  // is_official) už nejde „stiahnuť" späť na súkromnú — prepínač sa v takom prípade skrýva.
  const alreadyGlobal = editing && (challenge.is_public || challenge.is_official);
  const [visibility, setVisibility] = useState(editing ? (challenge.visibility || "private") : "private");
  const [catTried, setCatTried] = useState(false);  // G2a — pokus o odoslanie bez kategórie → zvýrazni výber piluliek
  const [lifeCatDialog, setLifeCatDialog] = useState(null);  // v32 — AI návrh kategórie { busy } | { category }
  const [inventOpen, setInventOpen] = useState(false);  // v2 — AI invent-whole dialog
  const [remindPrompt, setRemindPrompt] = useState(null);  // v2 — po vytvorení výzvy ponúkni pripomienku
  const [err, setErr] = useState("");
  const [descBusy, setDescBusy] = useState(false);   // v14 — field AI buttons
  async function aiFill(forceInvent) {
    setErr(""); setAiBusy(true);
    try {
      if (forceInvent || !name.trim()) {
        // v14 — no title: invent the WHOLE challenge/competition from the user's memory + data
        const r = await api("/api/ai/propose-challenge", { method: "POST", body: { is_competition: isComp, icons: SUGGEST_ICONS } });
        const ch = r.challenge || {};
        if (ch.name) setName(ch.name);
        if (ch.icon) setIcon(ch.icon);
        if (ch.desc) setDesc(ch.desc);
        if (isComp) {
          if (ch.metric_label) setMetricLabel(ch.metric_label);
          if (ch.metric_dir) setMetricDir(ch.metric_dir);
        } else if (ch.points) { setPts(ch.points); }
        if (ch.difficulty) setDifficulty(normDifficulty(ch.difficulty));
      } else {
        // draft the whole challenge/competition from the title: icon (from the set), description, points (výzva)
        const r = await api("/api/ai/estimate-habit", { method: "POST", body: { label: name, icons: SUGGEST_ICONS } });
        if (r.icon) setIcon(r.icon);
        if (r.desc !== undefined) setDesc(r.desc);
        if (!isComp && r.points) setPts(r.points);
        if (!isComp && r.difficulty) setDifficulty(normDifficulty(r.difficulty));
      }
    } catch (e) { setErr(e.message); }
    setAiBusy(false);
  }
  async function aiChDesc() {  // v14 — popis-only AI
    if (!name.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setDescBusy(true);
    try { const r = await api("/api/ai/estimate-habit", { method: "POST", body: { label: name, icons: SUGGEST_ICONS } }); if (r.desc) setDesc(r.desc); } catch (e) { setErr(e.message); }
    setDescBusy(false);
  }
  // f-batch #2 — `aiChPoints` (AI odhad BODOV výzvy) zmazaný spolu so svojím dialógom: od v29 §9.1
  // sa body výzvy odvodzujú od náročnosti (DifficultyPicker) a funkcia už nemala volajúceho.
  // v32 — AI návrh životnej kategórie (potvrdenie rovnaké ako pri bodoch)
  async function openLifeCatDialog() {
    if (!name.trim()) { setErr("Najprv napíš názov."); return; }
    setErr(""); setLifeCatDialog({ busy: true });
    try {
      const r = await api("/api/ai/suggest-life-category", { method: "POST", body: { label: name, desc } });
      if (!r.category) { setErr("AI nevedela kategóriu určiť — vyber ju ručne."); setLifeCatDialog(null); return; }
      setLifeCatDialog({ category: r.category });
    } catch (e) { setErr(e.message); setLifeCatDialog(null); }
  }
  const chHeaderAi = session.ai && !editing ? (
    <button type="button" onClick={() => setInventOpen(true)} title="Vymyslieť výzvu (AI)" aria-label="Vymyslieť"
      style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", alignItems: "center", cursor: "pointer", padding: 4 }}>
      <BuzerIcon name="sparkles" size={18} color="currentColor" /></button>
  ) : null;
  function close() { if (editing) { onDone && onDone(); } else { setOpen(false); } }
  // v2 — delete lives in the edit form (like the návyk edit modal), not in the detail
  async function del() {
    if (!confirm("Zmazať výzvu u teba? Ostatným účastníkom sa automaticky archivuje s výsledkami.")) return;
    try { await api("/api/challenges/" + challenge.id, { method: "DELETE" }); onAdded(); onDone && onDone(); } catch (e) { /* */ }
  }
  async function submit(e) {
    e.preventDefault(); setErr("");
    // G2a — životná kategória je POVINNÁ pre výzvu aj súťaž; bez nej neposielaj na API
    if (!category) { setCatTried(true); setErr("Vyber kategóriu — je povinná."); return; }
    setBusy(true);
    try {
      // feat/vyzva-email-invite — nezabudni na adresu, ktorú má človek ešte rozpísanú v poli
      const emails = inv.allEmails();
      const body = { name, icon, icon_color: iconColor || null, desc, points: pts, invite: inv.invite, invite_emails: emails, is_competition: isComp,
        ...(isComp ? {} : { difficulty }),
        category: category || null,  // v32 — životná kategória
        visibility: alreadyGlobal ? undefined : visibility,  // feat/challenge-visibility
        timer_sec: (Number(timerMin) || 0) * 60 + (Number(timerSec) || 0),  // pri stopkách sa countdown nepoužije, ale nenuluj ho
        result_timer: isComp && resultTimer,
        multi_day: isComp && multiDay,
        metric_label: (isComp && resultTimer) ? "čas" : metricLabel, metric_dir: metricDir, eval_date: isComp ? evalDate : "", duration_days: isComp ? 0 : (Number(duration) || 0),
        prev_id: prevId,  // v33 — na ktoré kolo nadväzuje (null = samostatné)
        // začiatok posiela len TVORBA (pri úprave by prepísal beh už zapojeným účastníkom)
        ...(editing || startDate === todayKey() ? {} : { start_date: startDate }),
        // súťaž frekvenciu nemá (nemá denné odškrtnutie) → rekurenciu neposielame vôbec
        ...(isComp ? {} : recurToBody(recur)) };
      let offer = null;
      if (editing) await api("/api/challenges/" + challenge.id, { method: "PATCH", body });
      else {
        const r = await api("/api/challenges", { method: "POST", body });
        // v2 — nová výzva (nie súťaž) → ponúkni pripomienku na jej backing tasku
        if (!isComp && r && r.task_id) offer = { taskId: r.task_id, label: name };
      }
      inv.reset({ keepFriends: editing });  // feat/vyzva-email-invite — pozvánky odišli
      if (!editing) { setName(""); setDesc(""); setPts(15); setDifficulty("normal"); setIcon(isComp ? "🥇" : "🤝"); setMetricLabel(""); setEvalDate(""); setTimerMin(0); setTimerSec(0); setResultTimer(false); setMultiDay(false); if (!offer) setOpen(false); }
      // zavretie + onAdded/onDone sa pri ponuke pripomienky ODKLADAJÚ až po popupe —
      // rodičia na ne unmountujú (napr. výzva z profilu priateľa) a popup by zmizol
      if (offer) { setRemindPrompt(offer); setBusy(false); return; }
      onAdded(); onDone && onDone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  // v2 — pripomienkový popup: kým je otvorený, formulár sa nezatvára (odložené callbacky vyššie)
  const remindPromptEl = remindPrompt ? (
    <ReminderPrompt taskId={remindPrompt.taskId} label={remindPrompt.label}
      onClose={() => { setRemindPrompt(null); setOpen(false); onAdded(); onDone && onDone(); }} />
  ) : null;
  if (!open) return <>{remindPromptEl}{controlled ? null : <button onClick={() => setOpen(true)} style={st.addToggle}><BuzerIcon name={isComp ? "trophy" : "flag"} size={16} /> Vytvoriť výzvu</button>}</>;
  const inModal = open;  // v2 — editing also renders as the same modal shell as create (just prefilled)
  return (
    <>
    {remindPromptEl}
    <FormShell modal={inModal} title={editing ? "Upraviť výzvu" : "Vytvoriť výzvu"} onClose={close} headerAction={inModal && !editing ? chHeaderAi : null}
      iconNode={<Glyph icon={icon || (isComp ? "trophy" : "flag")} size={20} />} iconStyle={iconChipStyle(iconColor)} onIconClick={() => setIconPick(true)}>
    <form onSubmit={submit} className={inModal ? "bz-modal-form" : undefined} style={inModal ? undefined : { ...st.card, marginTop: 12, ...st.formCol }}>
      {/* v26.2 §6 — Zapisovanie: Splnenie (odškrtnutie) / Vlastný výsledok (číslo). Po vytvorení nemenné.
          v34 — rovnaký Segmented ako Pripomienka/Udalosť vo formulári pripomienky (zdieľaný primitív). */}
      <Field label="Zapisovanie">
        <Segmented className="bz-seg--block" disabled={editing}
          options={[{ value: "done", label: "Splnenie" }, { value: "result", label: "Vlastný výsledok" }]}
          value={isComp ? "result" : "done"}
          onChange={(v) => { if (!editing) setIsComp(v === "result"); }} />
      </Field>
      <Field label={isComp ? "Názov (čo sa meria) *" : "Názov výzvy *"}>
        <Input placeholder={isComp ? "napr. Kto nabehá viac km" : "napr. 30 dní bez cukru"}
          value={name} onChange={(e) => setName(e.target.value)} required />
        {iconPick && (
          <IconColorModal icon={icon} iconColor={iconColor} fallback={isComp ? "trophy" : "flag"}
            onIcon={setIcon} onColor={setIconColor} onClose={() => setIconPick(false)} />
        )}
      </Field>
      <Field label="Špecifikácia">
        <Input placeholder="napr. kto vydrží dlhšie" value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      {/* v33 §4.2 — z čoho je toto pokračovanie. Pri ÚPRAVE ostáva dostupné: takto sa spája to,
          čo už beží (napr. septembrový Plank s augustovým). */}
      <PrevRoundField kind={isComp ? "comp" : "challenge"} value={prevId} onChange={setPrevId} />
      {/* v32 — ŽIVOTNÁ KATEGÓRIA (rovnaká sada + AI návrh ako pri návyku) */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>
          <span className="bz-field__label" style={{ marginBottom: 0 }}>Kategória *</span>
          {session.ai && <button type="button" onClick={openLifeCatDialog} title="Navrhnúť kategóriu (AI)" aria-label="Navrhnúť kategóriu"
            style={{ border: "none", background: "transparent", color: "var(--accent)", display: "inline-flex", cursor: "pointer", padding: 0 }}><BuzerIcon name="sparkles" size={15} color="currentColor" /></button>}
        </div>
        <div className={`bz-cat-pills${catTried && !category ? " bz-cat-pills--invalid" : ""}`}>
          {LIFE_CATS.map((c) => (
            <Pill key={c.key} on={category === c.key} onClick={() => { setCategory(category === c.key ? "" : c.key); setCatTried(false); }}>
              <BuzerIcon name={c.icon} size={15} color="currentColor" />{c.label}
            </Pill>
          ))}
        </div>
        {/* G2a — kategória je POVINNÁ pre výzvu aj súťaž; výrazné upozornenie kým nie je vybraná
            (nahradilo drobný Caption, ktorý bol prehliadnuteľný) */}
        {!category && (
          <div className="bz-cat-notice" role="alert">
            <span className="bz-cat-notice__dot" />Vyber kategóriu — je povinná.
          </div>
        )}
      </div>
      {/* feat/challenge-visibility — typ výzvy: súkromná (default, len pozvaní) / verejná (uzavretá
          — vidno ju verejne, vstup na žiadosť). Platí pre výzvu aj súťaž (rovnaký formulár) a
          nastavuje ju VÝHRADNE zakladateľ výzvy (formulár vidí len on).
          v28.9 — „oficiálna" už NIE JE nič, čo by si vedel niekto nastaviť: oficiálne sú výhradne
          kurátorské dávky zo `global_challenges.py` (systémový účet). Pri takej výzve sa prepínač
          skryje a ostane len informačný štítok. */}
      {alreadyGlobal ? (
        <Field label="Viditeľnosť">
          <Tag tone="brand" icon="globe">Oficiálna výzva</Tag>
          <Caption style={{ marginTop: 6 }}>Táto výzva je oficiálna — je natrvalo verejná a otvorená pre všetkých.</Caption>
        </Field>
      ) : (
        <Field label="Viditeľnosť">
          <Segmented className="bz-seg--block"
            options={[{ value: "private", label: "Súkromná" }, { value: "public", label: "Verejná" }]}
            value={visibility} onChange={setVisibility} />
          <Caption style={{ marginTop: 6 }}>
            {visibility === "public"
              ? "Uvidia ju všetci v „Prebiehajúcich výzvach“ aj na tvojom profile — vstup si musia vyžiadať, ty ho schváliš."
              : "Vidno ju len pozvaným — presne ako doteraz."}
          </Caption>
        </Field>
      )}
      {/* ZAČIATOK — spoločný pre výzvu aj súťaž (obe začínajú svojím `created_at`).
          Pri úprave sa neponúka: posun by pod rukami zmenil štart aj koniec tým,
          ktorí sa už zapojili. */}
      {!editing && (
        <Field label="Začiatok">
          <DateField value={startDate} onChange={setStartDate} label="Deň začiatku"
            min={todayKey()} max={shiftDay(todayKey(), START_MAX_AHEAD_DAYS)} />
          {startDate !== todayKey() && (
            <Caption>{isComp ? "Súťaž" : "Výzva"} sa spustí {fmtDuePretty(startDate)} — dovtedy pri nej beží odpočet do štartu.</Caption>
          )}
        </Field>
      )}
      {isComp ? (
        <>
          {/* stopky/režim dňa sa dajú zmeniť aj po vytvorení — doterajšie zápisy (výsledky) sa NEmažú.
              Pri zapnutí stopiek sa existujúce čísla čítajú ako čas (sekundy). */}
          <label style={st.inlineLabel}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Stopky merajú výsledok</span>
            <button type="button" role="switch" aria-checked={resultTimer}
              onClick={() => setResultTimer((v) => { if (!v) setMultiDay(true); return !v; })}
              style={{ ...st.switch, flex: "none", cursor: "pointer", ...(resultTimer ? st.switchOn : {}) }}><span style={{ ...st.switchKnob, ...(resultTimer ? st.switchKnobOn : {}) }} /></button>
          </label>
          {resultTimer ? (
            <div style={st.taskDesc}>Účastník spustí stopky, po zastavení sa čas uloží ako výsledok (napr. 4:35 alebo 1:23:45).</div>
          ) : (
            <Input placeholder="Čo sa meria? (napr. čas v s, počet, km)"
              value={metricLabel} onChange={(e) => setMetricLabel(e.target.value)} />
          )}
          {/* režim dňa: jeden zápis (prepíše sa) vs. viac zápisov (sčítavajú sa) — meniteľné aj po vytvorení */}
          <Field label="Zápis za deň">
            <div style={{ display: "flex", gap: 6, background: "var(--neutral-surface-3)", padding: 4, borderRadius: "var(--radius-pill)" }}>
              {[[false, "Jeden"], [true, "Viac — sčítavajú sa"]].map(([val, lbl]) => {
                const active = multiDay === val;
                return (
                  <button type="button" key={String(val)} onClick={() => setMultiDay(val)}
                    style={{ flex: 1, textAlign: "center", fontSize: 12.5, fontWeight: 600, padding: "9px 0", borderRadius: "var(--radius-pill)", cursor: "pointer", border: "none", fontFamily: "var(--ff-ui)", WebkitTextFillColor: "currentColor",
                      ...(active ? { background: "var(--neutral-surface)", color: "var(--neutral-text-main)", boxShadow: "var(--elev-1)" } : { background: "transparent", color: "var(--neutral-text-muted)" }) }}>{lbl}</button>
                );
              })}
            </div>
            <div style={{ ...st.taskDesc, marginTop: 6 }}>
              {multiDay ? "Každý zápis sa pripočíta k dnešku — napr. viac tréningov či plankov za deň."
                : "Nový zápis prepíše ten dnešný — napr. najlepší výkon v jednom pokuse."}
            </div>
            {editing && <div style={{ ...st.taskDesc, marginTop: 6, color: "var(--ember)" }}>Doterajšie zápisy zostanú zachované. Pri zapnutí stopiek sa existujúce čísla zobrazia ako čas (sekundy).</div>}
          </Field>
          <label style={st.inlineLabel}><span>Vyhráva</span>
            <Select style={{ width: 170 }}
              value={metricDir} onChange={(e) => setMetricDir(e.target.value)}>
              <option value="high">vyšší výsledok</option>
              <option value="low">nižší výsledok</option>
            </Select></label>
          <label style={st.inlineLabel}><span>Dátum vyhodnotenia</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {/* v26.3 §1 — kalendárový picker (čistý dátum) */}
              <DateField value={evalDate} onChange={setEvalDate} allowClear label="Dátum vyhodnotenia" />
            </span></label>
          {/* v29 §9.3.5 — „Body pre víťaza" si zakladateľ už nenastavuje (dalo sa tým farmiť body
              a odmena nesúvisela s odvedenou prácou). Odmena sa počíta až pri vyhodnotení a dostane
              ju každý účastník so zápisom, podľa toho, koľko odrobil. */}
          <Field label="Odmena">
            <Caption>Odmenu vypočítame po vyhodnotení podľa toho, ako dlho a ako náročne sa súťažilo.</Caption>
          </Field>
        </>
      ) : (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {/* v29 §9.1 — náročnosť výzvy nahradila voľný číselník bodov; body sú z nej odvodené
              (5/10/20) a pri vstupe sa skopírujú do podkladového návyku každého člena. */}
          <Field label="Náročnosť" style={{ flex: "1 1 100%", minWidth: 0 }}>
            <DifficultyPicker value={difficulty} onChange={setDifficulty} />
          </Field>
          <Field label="Časovač" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Input style={{ minWidth: 0 }} type="number" min="0" max="600" value={timerMin} onChange={(e) => setTimerMin(e.target.value)} aria-label="minúty" />
              <span style={st.taskDesc}>min</span>
              <Input style={{ minWidth: 0 }} type="number" min="0" max="59" value={timerSec} onChange={(e) => setTimerSec(e.target.value)} aria-label="sekundy" />
              <span style={st.taskDesc}>s</span>
            </div>
          </Field>
        </div>
      )}
      {!isComp && (<>
        {/* feat/vyzva-frekvencia — TEN ISTÝ ovládač ako pri návyku (zdieľaný `RecurrenceField`),
            takže výzva vie aj „N× za mesiac/pol rok/rok" a „viackrát za deň". Zmena frekvencie
            bežiacej výzvy sa prepíše do podkladového návyku KAŽDÉHO účastníka — preto varovanie. */}
        <RecurrenceField value={recur} onChange={setRecur}
          rareHint={'Zriedkavá výzva — na Dnes bude v sekcii „Máš čas" pod bežnými návykmi.'}
          hint={recurChanged && (memberIds || []).length > 1
            ? <Caption style={{ marginTop: 6 }}>Zmena frekvencie sa prepíše do návyku všetkých účastníkov výzvy. Doterajšie splnenia ostávajú.</Caption>
            : null} />
        <label style={st.inlineLabel}><span>Trvanie</span>
          <Select style={{ width: 150 }} value={durCustom ? "custom" : String(duration)}
            onChange={(e) => { const v = e.target.value; if (v === "custom") { setDurCustom(true); } else { setDurCustom(false); setDuration(Number(v)); } }}>
            <option value="0">Bez limitu</option>
            {DUR_OPTIONS.map(([d, lbl]) => <option key={d} value={String(d)}>{lbl}</option>)}
            <option value="custom">Vlastné…</option>
          </Select></label>
        {durCustom && (
          <label style={st.inlineLabel}><span>Počet dní</span>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Input style={{ width: 90 }} type="number" min="1" max="365"
                value={duration} onChange={(e) => setDuration(e.target.value)} aria-label="počet dní" />
              <span style={st.taskDesc}>dní</span>
            </span></label>
        )}
      </>)}
      {isComp && !resultTimer && (
        <label style={st.inlineLabel}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><BuzerIcon name="stopwatch" size={16} color="var(--text2)" /> Časovač</span>
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Input style={{ width: 64 }} type="number" min="0" max="600"
              value={timerMin} onChange={(e) => setTimerMin(e.target.value)} aria-label="minúty" />
            <span style={st.taskDesc}>min</span>
            <Input style={{ width: 64 }} type="number" min="0" max="59"
              value={timerSec} onChange={(e) => setTimerSec(e.target.value)} aria-label="sekundy" />
            <span style={st.taskDesc}>s</span>
          </span></label>
      )}
      {/* správa účastníkov je len tu, v Upraviť (v rebríčku sa dalo × ťuknúť omylom) */}
      {editing && isCreator && <ParticipantsEdit cid={challenge.id} members={members} onChanged={onAdded} />}
      {/* feat/globalne-pozvanky — polia „Pozvať priateľov" + „Pozvať e-mailom" sú zdieľané
          so sheetom „Pozvať" v detaile globálnej výzvy (ChallengeInviteFields) */}
      <ChallengeInviteFields inv={inv} editing={editing} />
      {err && <div style={st.err}>{err}</div>}

      {inventOpen && (
        <Modal center title="Vymyslieť výzvu?" icon="sparkles" onClose={() => { if (!aiBusy) setInventOpen(false); }}>
          <div style={{ fontSize: 14, color: "var(--neutral-text-muted)", lineHeight: 1.5 }}>Chceš, aby ti AI vymyslela celú výzvu? Na základe tvojich údajov navrhne názov, popis aj body.</div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setInventOpen(false)} disabled={aiBusy}>Zrušiť</Button>
            <Button variant="primary" block disabled={aiBusy} onClick={async () => { await aiFill(true); setInventOpen(false); }}><BuzerIcon name="sparkles" size={16} color="currentColor" />{aiBusy ? "Vymýšľam…" : "Vymyslieť"}</Button>
          </div>
        </Modal>
      )}
      {lifeCatDialog && (
        <Modal center title="AI navrhuje kategóriu" icon="sparkles" maxWidth={360} onClose={() => setLifeCatDialog(null)}>
          <div style={{ textAlign: "center", padding: "4px 0 2px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 24, color: "var(--text)", display: "inline-flex", alignItems: "center", gap: 8 }}>
              {lifeCatDialog.busy ? "…" : (<>
                <BuzerIcon name={(LIFE_CAT[lifeCatDialog.category] || {}).icon || "label"} size={22} color="currentColor" />
                {(LIFE_CAT[lifeCatDialog.category] || {}).full || lifeCatDialog.category}
              </>)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setLifeCatDialog(null)}>Zrušiť</Button>
            <Button variant="primary" block disabled={lifeCatDialog.busy} onClick={() => { if (lifeCatDialog.category) setCategory(lifeCatDialog.category); setLifeCatDialog(null); }}>Aplikovať</Button>
          </div>
        </Modal>
      )}
      {/* f-batch #2 — dialóg „AI navrhuje X bodov" je preč: od v29 §9.1 si výzva body nenastavuje
          (nahradila ich náročnosť) a jeho spúšťač (`aiChPoints`) už nikde nevisel — bol to mŕtvy kód,
          ktorý navyše ukazoval body ignorujúce koeficient série. */}
      {/* G2a — kategóriu už NEblokujeme cez disabled (dead-click bez odozvy); povinnosť vynúti submit()
          handler s výrazným upozornením + zvýraznením výberu */}
      <FormFooter submitLabel={busy ? "…" : editing ? "Uložiť" : "Vytvoriť výzvu"} submitDisabled={busy || !name.trim()}
        deleteLabel="Zmazať výzvu" onDelete={editing ? del : undefined} onCancel={close} />
    </form>
    </FormShell>
    </>
  );
}

// TODO(v29-open): `CompetitionDetail` nedostáva `lite` (rodič ho neposiela) → štítok série ide
// v Lite v streak-oranžovej namiesto farby témy. Viď v29-open-questions.
// v33 — „Viac" (⋮) v hlavičke detailu, naľavo od chatu. Presúva sem nastavenia, ktoré detail
// modal len naťahovali a človek ich mení zriedka: pripomenutie, skupina, náročnosť. Každá položka
// otvorí vlastný malý sheet, nech je na obrazovke naraz práve jedna vec.
// Súťaž nemá podkladový návyk → nemá ani pripomenutie a náročnosť; ostáva len skupina.
export function DetailMoreMenu({ isComp, cid, taskId, remindAt, difficulty, streak, categoryId, categories, lite, reload }) {
  const [menu, setMenu] = useState(false);
  const [pane, setPane] = useState(null);   // "remind" | "group" | "difficulty"
  const [cat, setCat] = useState(undefined);
  const [diff, setDiff] = useState(undefined);
  const items = [];
  if (taskId) items.push({ icon: "bell", label: "Upozorniť ma v čase", desc: remindAt ? "Zapnuté na " + remindAt : "Vypnuté", onClick: () => setPane("remind") });
  if ((categories || []).length) {
    const curId = cat !== undefined ? cat : categoryId;
    const curName = (categories.find((c) => c.id === curId) || {}).name;
    items.push({ icon: "folder", label: "Skupina", desc: curName || "Bez skupiny", onClick: () => setPane("group") });
  }
  if (taskId && !lite) items.push({ icon: "star", label: "Náročnosť", desc: DIFF_SK[normDifficulty(diff !== undefined ? diff : difficulty)], onClick: () => setPane("difficulty") });
  if (!items.length) return null;
  const close = () => setPane(null);
  return (
    <>
      <IconButton icon="dots" title="Viac" onClick={() => setMenu(true)} />
      {menu && <ActionMenu title="Nastavenia" items={items} onClose={() => setMenu(false)} />}
      {pane === "remind" && (
        <Sheet center title="Upozorniť ma v čase" icon="bell" onClose={close}>
          <ChallengeRemind key={"rem" + taskId} taskId={taskId} value={remindAt} />
        </Sheet>
      )}
      {pane === "group" && (
        <Sheet center title="Skupina" icon="folder" onClose={close}>
          <GroupPicker categories={categories} value={cat !== undefined ? cat : categoryId}
            onChange={async (v) => {
              if (isComp) await api("/api/challenges/" + cid + "/group", { method: "POST", body: { category_id: v } });
              else await api("/api/tasks/" + taskId, { method: "PATCH", body: { category_id: v } });
              setCat(v); reload && reload();
            }} />
        </Sheet>
      )}
      {pane === "difficulty" && (
        <Sheet center title="Tvoja náročnosť" icon="star" onClose={close}>
          <DifficultyPicker value={diff !== undefined ? diff : difficulty} streak={streak || 0}
            onChange={async (v) => {
              await api("/api/tasks/" + taskId, { method: "PATCH", body: { difficulty: v } });
              setDiff(v); reload && reload();
            }} />
        </Sheet>
      )}
    </>
  );
}

// v34 — VÝHRA (cena z kolesa) ako DLAŽDICA vedľa odpočtu v záložke Rebríček (majiteľka
// 2026-09-05: „dni do konca a výhra do jedného riadku vedľa seba"). Dlaždica ukazuje len stav
// („Hrá sa o cenu" / „Zatiaľ bez ceny" / „Cena sa neudeľuje"); ťuknutie rozbalí pod riadkom
// dôvod a podmienky (PrizeDetails). Podmienky ostávajú vidieť aj POČAS súťaže (v37), nech
// nikto nezistí až na konci, že o cenu nehral. Stav `open` drží CompetitionDetail — telo sa
// kreslí mimo mriežky dlaždíc.
function prizeState(data, evaluated) {
  const ok = !!data.prize_ok && !evaluated;
  if (!ok && !data.prize_reason) return null;
  return {
    ok,
    title: ok ? "Hrá sa o cenu" : (evaluated ? "Cena sa neudeľuje" : "Zatiaľ bez ceny"),
    hint: ok ? "koleso výhry" : "výhra · prečo?",
  };
}

function PrizeTile({ data, evaluated, open, onToggle }) {
  const p = prizeState(data, evaluated);
  if (!p) return null;
  return (
    <button type="button" className={"bz-comp-tile bz-comp-tile--btn" + (p.ok ? " bz-comp-tile--ok" : "")}
      aria-expanded={open} onClick={onToggle}>
      <span className="bz-comp-tile__val">{p.title}</span>
      <span className="bz-comp-tile__lbl"><BuzerIcon name="gift" size={12} color="currentColor" />{p.hint}</span>
    </button>
  );
}

function PrizeDetails({ data, evaluated }) {
  const p = prizeState(data, evaluated);
  if (!p) return null;
  const rules = data.prize_rules || [];
  return (
    <div className="bz-comp-prize__body">
      {p.ok
        ? <p className="bz-comp-prize__reason">Súťaž spĺňa podmienky — víťaz si po vyhodnotení zatočí kolesom o jednu z cien.</p>
        : <p className="bz-comp-prize__reason">{data.prize_reason}</p>}
      {rules.length > 0 && (
        <ul className="bz-comp-prize__rules">
          {rules.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
    </div>
  );
}

export function CompetitionDetail({ comp, reload, onClose, lite = false }) {
  const [nextRound, setNextRound] = useState(false);  // v33 §4.1 — „Ďalšie kolo"
  const { open: openPublicProfile } = useProfileModal();  // globálny modal verejného profilu
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);  // feat/globalne-pozvanky — sheet „Pozvať"
  // v34 — záložky detailu: „board" = Rebríček (odpočet + poradie), „info" = Podrobnosti
  // (popis, kategória, kolá, cena, moje čísla)
  const [tab, setTab] = useState("board");
  // v34 — spodná lišta: kalendár na dopisovanie minulých dní je schovaný za odkazom
  const [past, setPast] = useState(false);
  // v34 — dlaždica Výhra v Rebríčku: rozbalený dôvod + podmienky pod riadkom dlaždíc
  const [prizeOpen, setPrizeOpen] = useState(false);
  // v32 — graf sa ukáže až po kliknutí na účastníka v rebríčku (user_id | null); klik znova = zavrieť
  const [selectedMember, setSelectedMember] = useState(null);
  const [result, setResult] = useState("");        // value being entered
  const [editToday, setEditToday] = useState(true); // show the input until the day's entry exists
  // dopisovanie za MINULÉ dni: deň zápisu (predvolene dnes). Server ho overí voči behu súťaže
  // (nie do budúcnosti, nie pred štartom, nie po konci) — tu ho len ohraničíme v kalendári.
  const [day, setDay] = useState(todayKey());
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // v2 — multi_day: „Opraviť dnešok" prepíše; ref drží aktuálnu hodnotu pre load() (stale closure)
  const [fixToday, setFixToday] = useState(false);
  const fixTodayRef = useRef(false);
  useEffect(() => { fixTodayRef.current = fixToday; }, [fixToday]);
  // `load` číta vybraný deň cez ref (nie cez závislosť), nech prepnutie dňa nespôsobí refetch
  const dayRef = useRef(day);
  useEffect(() => { dayRef.current = day; }, [day]);
  const load = useCallback(async () => {
    const d = await api("/api/challenges/" + comp.id);
    setData(d);
    // v2 — multi_day: input slúži na PRIDANIE záznamu → nechaj ho prázdny;
    // počas „Opraviť dnešok" doň ale nesiahaj (stopky/refresh by zmazali rozpísanú hodnotu)
    if (d.multi_day) { if (!fixTodayRef.current) setResult(""); return; }
    // v2 — stopky: hodnoty sú sekundy → do inputu predvyplň čitateľný čas (4:35)
    const v = d.my_days ? d.my_days[dayRef.current] : null;
    if (v != null) { setResult(d.result_timer ? fmtDur(v) : String(v)); setEditToday(false); }
    else { setResult(""); setEditToday(true); }
  }, [comp.id]);
  useEffect(() => { load(); }, [load]);
  // prepnutie dňa prepíše rozpísaný vstup hodnotou toho dňa — inak by „Uložiť" prepísalo
  // iný deň číslom, ktoré na obrazovke patrilo dnešku
  useEffect(() => {
    setFixToday(false); setSaved(false); setErr("");
    if (!data) return;
    if (data.multi_day) { setResult(""); return; }
    const v = data.my_days ? data.my_days[day] : null;
    if (v != null) { setResult(data.result_timer ? fmtDur(v) : String(v)); setEditToday(false); }
    else { setResult(""); setEditToday(true); }
  }, [day]);  // eslint-disable-line react-hooks/exhaustive-deps
  const closed = comp.status === "closed";
  const me = data && data.members.find((m) => m.is_me);
  // v29 §9.3.5 — odmena existuje až po vyhodnotení. Stav berieme z čerstvo načítaného detailu
  // (`data.status`), nie z `comp` v rodičovi — ten sa po „Vyhodnotiť" obnoví až s ďalším pollom.
  const evaluated = !!(data && (data.status || comp.status) === "closed");
  const winner = (data && data.awarded_uid) ? data.members.find((m) => m.user_id === data.awarded_uid) : null;
  // v2 — stopky merajú výsledok: hodnoty sú sekundy, zobrazujú sa ako čas (4:35 / 1:23:45)
  const isTimerComp = !!((data && data.result_timer) || comp.result_timer);
  const fmtVal = (v) => (isTimerComp ? fmtDur(v) : v);
  // v2 — multi_day: zápisy za deň sa sčítavajú (Pridať), oprava dňa prepíše
  const multiDay = !!((data && data.multi_day) || comp.multi_day);
  // vybraný deň: čo je zaň zapísané (null = nič) + jeho pomenovanie do copy („dnes"/„včera"/„12. júl")
  const isToday = day === todayKey();
  const dayVal = (data && data.my_days && data.my_days[day] != null) ? data.my_days[day] : null;
  const dayName = chatDayHeading(day, todayKey()).toLowerCase();
  const dayLabel = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  // uzavretá súťaž už Stopwatch nerenderuje → zahoď prípadné visiace meranie z localStorage
  useEffect(() => { if (closed) { localStorage.removeItem("buzer_stopwatch_comp" + comp.id); localStorage.removeItem("buzer_stopwatch_comp" + comp.id + "_stop"); } }, [closed, comp.id]);

  async function saveResult(e, add = false) {
    e.preventDefault(); setErr("");
    const val = isTimerComp ? parseDur(result) : parseFloat(result);
    if (result === "" || isNaN(val)) { setErr(isTimerComp ? "Zadaj čas ako 4:35, 1:23:45 alebo počet sekúnd." : "Zadaj číslo."); return; }
    setBusy(true);
    try {
      await api("/api/challenges/" + comp.id + "/result", { method: "POST", body: { result: val, add, day } });
      setSaved(true); setEditToday(false); setFixToday(false);
      if (add) setResult("");  // multi_day: input je na ďalší záznam
      load(); reload();
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  // v2 — stop na stopkách: multi_day pripočíta k dnešku, inak dnešok prepíše
  async function stopwatchSave(sec) {
    setErr("");
    try {
      await api("/api/challenges/" + comp.id + "/result", { method: "POST", body: { result: sec, add: multiDay, day } });
      setSaved(true); load(); reload();
      setTimeout(() => setSaved(false), 2200);
    } catch (e) { setErr(e.message); throw e; }
  }
  async function toggleClose() { await api("/api/challenges/" + comp.id + "/close", { method: "POST" }); load(); reload(); }
  // v32 — opustenie aj zmazanie idú cez ConfirmDelete modal (archív ako bezpečnejšia voľba)
  const [leaveAsk, setLeaveAsk] = useState(false);
  const [delAsk, setDelAsk] = useState(false);
  async function archiveMe() { await api("/api/challenges/" + comp.id + "/archive", { method: "POST", body: { scope: "me" } }); reload(); onClose && onClose(); }
  async function leave() { await api("/api/challenges/" + comp.id + "/leave", { method: "DELETE" }); reload(); onClose && onClose(); }
  async function archiveAll() { await api("/api/challenges/" + comp.id + "/archive", { method: "POST", body: { scope: "all" } }); reload(); onClose && onClose(); }
  async function del() { await api("/api/challenges/" + comp.id, { method: "DELETE" }); reload(); onClose && onClose(); }

  if (nextRound && data) {
    return (
      <ChallengeCreate preset={{ id: comp.id, name: data.name, icon: data.icon, icon_color: data.icon_color,
        desc: data.descr, is_competition: true, metric_label: data.metric_label, metric_dir: data.metric_dir,
        result_timer: data.result_timer, multi_day: data.multi_day, category: data.category }}
        initialInvite={(data.members || []).filter((m) => !m.is_me).map((m) => m.user_id)}
        onAdded={() => { reload(); }} onDone={() => { setNextRound(false); onClose && onClose(); }} />
    );
  }
  if (editing && data) {
    return (
      <ChallengeCreate challenge={{ id: comp.id, name: data.name, icon: data.icon, icon_color: data.icon_color, desc: data.descr,
        points: data.points, is_competition: true, metric_label: data.metric_label, category: data.category,
        metric_dir: data.metric_dir, eval_date: data.eval_date, timer_sec: data.timer_sec, result_timer: data.result_timer, multi_day: data.multi_day,
        visibility: data.visibility, is_public: data.is_public, is_official: data.is_official }}  // feat/challenge-visibility
        memberIds={data.members.map((m) => m.user_id)}
        members={data.members} isCreator={data.is_creator}
        onAdded={() => { load(); reload(); }} onDone={() => setEditing(false)} />
    );
  }

  // v32 — 7-dňový graf účastníka (rovnaký chart element, aký mal „Tvoje záznamy · 7 dní");
  // renderuje sa priamo pod kliknutým riadkom rebríčka
  const memberChart = (m) => {
    const hist = m.history || {};
    const days = Object.keys(hist).sort();
    if (!days.length) return null;
    const maxV = Math.max(1, ...days.map((k) => hist[k]));
    const lbls = ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"];
    return (
      <div style={{ margin: "-4px 0 10px" }}>
        <SectionHeader label={(m.is_me ? "Tvoje záznamy" : m.name) + " · 7 dní"} style={{ margin: "6px 2px 4px" }}
          trailing={m.result != null ? <span style={{ ...st.taskDesc, fontFamily: "var(--font-mono)", fontWeight: 700 }}>súčet {fmtVal(m.result)}</span> : null} />
        {/* v18 — chart inside a bordered card (mock #12) */}
        <div style={{ background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: 18, padding: "16px 12px 10px" }}>
          <div style={st.chart}>
            {days.map((k) => {
              const v = hist[k];
              const dt = new Date(k + "T00:00:00");
              return (
                <div key={k} style={st.chartCol}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text2)" }}>{v ? fmtVal(v) : ""}</span>
                  <div style={st.barTrack}><div style={{ ...st.bar, height: `${Math.max(4, (v / maxV) * 100)}%`, background: v > 0 ? "var(--accent-grad)" : "var(--accent-tint)" }} /></div>
                  <span style={st.chartLabel}>{lbls[dt.getDay()]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // v34 — „Pridať za minulý deň": ukáž kalendár a rovno predvoľ včerajšok (ak už súťaž bežala) —
  // kto na odkaz ťukne, chce minulý deň, nie dnešok znova
  const startPast = () => {
    setPast(true);
    const y = shiftDay(todayKey(), -1);
    if (!data || !data.start_day || y >= data.start_day) setDay(y);
  };
  // v34 — spodná lišta: pomocný riadok „popis vľavo · odkaz na minulý deň vpravo"
  const labelRow = (text) => (
    <div className="bz-comp-lbl">
      <Caption>{text}</Caption>
      {!past && <button type="button" className="bz-comp-pastlink" onClick={startPast}>Pridať za minulý deň</button>}
    </div>
  );

  return (
    <div>
      {/* v34 — detail súťaže má dve záložky (rozhodnutie majiteľky 2026-09-05): REBRÍČEK
          (odpočet ako pásik + poradie) a PODROBNOSTI (popis, kategória, kolá, cena, moje čísla).
          Dlaždice MINULE / DO KONCA / ODMENA sú preč — brali miesto a rebríček bol až dole. */}
      <Segmented className="bz-seg--block bz-comp-tabs" value={tab} onChange={setTab}
        options={[{ value: "board", label: "Rebríček" }, { value: "info", label: "Podrobnosti" }]} />

      {data && tab === "board" && (
        <>
          {/* Riadok dvoch dlaždíc: ODPOČET a VÝHRA vedľa seba (majiteľka 2026-09-05).
              Súťaž sa vyhodnocuje k dátumu, takže namiesto hrubého „ostáva X dní" beží ŽIVÝ
              ODPOČET do konca posledného dňa — je hneď vidieť, koľko času na zápis ostáva.
              Výhra je tlačidlo: rozbalí dôvod + podmienky pod riadkom (PrizeDetails). */}
          {(() => {
            const hasCount = !evaluated && (data.ends_at || data.starts_at || data.days_left != null);
            const hasPrize = !!prizeState(data, evaluated);
            if (!hasCount && !hasPrize) return null;
            return (
              <>
                <div className="bz-comp-tiles">
                  {hasCount && (
                    <div className="bz-comp-tile">
                      <span className="bz-comp-tile__val bz-comp-tile__val--time">
                        {(data.ends_at || data.starts_at)
                          ? <Countdown endsAt={data.ends_at} startsAt={data.starts_at} />
                          : `${data.days_left} ${data.days_left === 1 ? "deň" : (data.days_left >= 2 && data.days_left <= 4 ? "dni" : "dní")}`}
                      </span>
                      <span className="bz-comp-tile__lbl"><BuzerIcon name="hourglass" size={12} color="currentColor" />{data.starts_at ? "do štartu" : "do konca"}</span>
                    </div>
                  )}
                  {hasPrize && <PrizeTile data={data} evaluated={evaluated} open={prizeOpen} onToggle={() => setPrizeOpen(!prizeOpen)} />}
                </div>
                {prizeOpen && hasPrize && <PrizeDetails data={data} evaluated={evaluated} />}
              </>
            );
          })()}
          {/* v29 §9.3.4/§9.3.5 — po vyhodnotení: kto vyhral, koľko dostal, či pribudol záchranný kruh
              a POVINNÉ vysvetlenie (`award_note`), prečo odmena vyšla práve takto. Bez neho by
              AI-vážená odmena pôsobila ako svojvôľa. Vidia ho všetci účastníci. */}
          {evaluated && (
            <div style={{ ...st.infoNote, marginTop: 12, marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <BuzerIcon name="trophy" size={16} color="var(--brand-primary)" />
                <strong style={{ color: "var(--text)" }}>{winner ? (winner.is_me ? "Vyhral/a si" : winner.name) : "Bez víťaza"}</strong>
                {(data.awarded_pts || 0) > 0 && <Tag tone="brand" size="xs">+{data.awarded_pts} b</Tag>}
                {(data.awarded_freezes || 0) > 0 && (
                  <Tag tone="info" size="xs" icon="shield">{data.awarded_freezes}× záchranný kruh</Tag>
                )}
              </div>
              {data.award_note && <div style={{ marginTop: 7 }}>{data.award_note}</div>}
              {/* odmenu po novom dostáva KAŽDÝ účastník so zápisom (§9.3.6), nielen víťaz */}
              {data.is_creator && !data.is_official && (
                <div style={{ marginTop: 12 }}>
                  <Button variant="ghost" size="sm" onClick={() => setNextRound(true)}>
                    <BuzerIcon name="refresh" size={15} color="currentColor" />Ďalšie kolo
                  </Button>
                </div>
              )}
              {me && !(winner && winner.is_me) && (
                <div style={{ marginTop: 7 }}>
                  {(data.my_awarded_pts || 0) > 0
                    ? <>Tvoja odmena za odvedenú prácu: <strong style={{ color: "var(--text)" }}>+{data.my_awarded_pts} b</strong>.</>
                    : <>Tebe odmena nevyšla — body sa udeľujú za dni so zápisom a súťaž potrebuje aspoň dvoch súťažiacich.</>}
                </div>
              )}
            </div>
          )}
          {/* feat/challenge-visibility — čakajúce žiadosti o vstup (len zakladateľ, uzavretá verejná) */}
          {data.is_creator && <FounderRequests cid={comp.id} requests={data.pending_requests} onChanged={load} />}
          {/* feat/globalne-pozvanky — do globálnej (oficiálnej) súťaže smie pozývať každý účastník */}
          <SectionHeader label="Rebríček · podľa súčtu" icon="trophy" style={{ margin: "16px 2px 12px" }}
            trailing={data.can_invite && !data.is_creator ? (
              <Button variant="ghost" size="sm" onClick={() => setInviting(true)}>
                <BuzerIcon name="people" size={15} color="currentColor" />Pozvať
              </Button>
            ) : null} />
          {inviting && (
            <ChallengeInviteSheet cid={comp.id} name={data.name} isComp
              memberIds={(data.members || []).map((m) => m.user_id)}
              onClose={() => setInviting(false)} onInvited={load} />
          )}
          <div>
            {data.members.map((m) => {
              const sel = selectedMember === m.user_id;
              const leaderResult = data.members.find((x) => x.rank === 1)?.result;
              // v33 — okrem počtu záznamov vidno aj dnešný prírastok a (pri ostatných účastníkoch)
              // odstup za vedúcim.
              // f-batch #4 — CELKOVÝ SÚČET sa z meta riadka VYHODIL: to isté číslo visí vpravo
              // v riadku, takže sa výsledok písal dvakrát. Vpravo ostáva, tu nie.
              // TODO(v29-open): s ním z riadka zmizla aj jednotka (metric_label) — či ju pridať
              // k hodnote vpravo, čaká na potvrdenie (docs/v29-open-questions.md).
              const metaTxt = m.result != null ? [
                (m.entries || 0) + "×",
                m.today != null ? "dnes " + fmtVal(m.today) : null,
                (m.rank !== 1 && leaderResult != null) ? fmtVal(Math.abs(leaderResult - m.result)) + " za vedúcim" : null,
              ].filter(Boolean).join(" · ") : "zatiaľ bez záznamu";
              return (
                <div key={m.user_id}>
                  {/* v32 — klik na riadok prepína 7-dňový graf účastníka (namiesto otvárania profilu) */}
                  <div role="button" tabIndex={0} aria-expanded={sel} aria-label={"Zobraziť záznamy: " + m.name}
                    onClick={() => setSelectedMember((p) => (p === m.user_id ? null : m.user_id))}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedMember((p) => (p === m.user_id ? null : m.user_id)); } }}
                    style={{ display: "flex", alignItems: "center", gap: 11, padding: "14px 14px", borderRadius: 16, marginBottom: 10, cursor: "pointer", border: "1px solid " + (sel ? "var(--accent)" : (m.is_me || m.is_winner ? "var(--accent-tint)" : "var(--border)")), background: m.is_me || m.is_winner ? "var(--ember-soft)" : "var(--surface-solid)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 8, flexShrink: 0, fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 12.5,
                      ...(m.rank === 1 ? { background: "var(--accent)", color: "var(--on-ember)" } : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text2)" }) }}>{m.is_winner ? "🏆" : (m.rank || "–")}</span>
                    {/* B6 — klik na avatar otvorí verejný profil (bez prepnutia grafu) */}
                    <Avatar user={m} size={30} onClick={(e) => { e.stopPropagation(); openPublicProfile(m.user_id); }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* v29 §10 — séria súťaže (dni so zápisom po sebe) hneď vedľa mena;
                          sivá, kým sa dnes nezapísalo, farebná po dnešnom zápise (§3.5) */}
                      <BoardName name={m.name} isMe={m.is_me}
                        streak={m.streak} unit={m.streak_unit} extended={m.streak_extended_today} />
                      <div style={st.taskDesc}>{metaTxt}</div>
                    </div>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, fontWeight: 800, flexShrink: 0, color: m.rank === 1 ? "var(--accent)" : "var(--text2)" }}>{m.result != null ? fmtVal(m.result) : "–"}</span>
                    <Icon name="chevron" size={15} color="var(--muted)" style={{ flexShrink: 0, transform: sel ? "rotate(90deg)" : "none", transition: "transform .18s ease" }} />
                  </div>
                  {sel && memberChart(m)}
                </div>
              );
            })}
          </div>
          {data.invited_count > 0 && <div style={{ ...st.taskDesc, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 5 }}><BuzerIcon name="hourglass" size={13} color="currentColor" />{data.invited_count} pozvaných</div>}

          {/* v33h — tvorcove akcie (Upraviť/Vyhodnotiť/kôš) sú v sticky footeri nižšie; tu ostáva
              len „Opustiť" pre nečlena-tvorcu */}
          {!comp.is_creator && (
            <div style={{ borderTop: "1px solid var(--neutral-border)", marginTop: 18, paddingTop: 14, display: "flex", gap: 8 }}>
              <Button variant="ghost" block onClick={() => setLeaveAsk(true)} style={{ color: "var(--danger)" }}><Icon name="logout" size={16} color="currentColor" />Opustiť výzvu</Button>
            </div>
          )}
        </>
      )}

      {data && tab === "info" && (
        <>
          {/* v26 §3.12 — popis v rámiku hore */}
          <DescrBox text={data.descr} style={{ marginTop: 12 }} />
          {/* v33 §6.2 — méta na prekonanie + TIEŇ minulého ja (rozbaľovacia sekcia kôl).
              Stupnica lišty ide od 0 po métu (prípadne po rekord, keď je vyšší), takže „plná
              lišta" doslova znamená „prekonané". Text nikdy nekomentuje zaostávanie ako zlyhanie
              — hovorí, koľko chýba (§0 princíp 3). */}
          {data.round_no > 1 && data.prev && <RoundSection data={data} fmtVal={fmtVal} />}
          {/* v34 — moje čísla: ten istý dizajn ako „Tvoje čísla" pri merateľnom návyku */}
          <CompetitionStats data={data} unit={isTimerComp ? "" : (data.metric_label || "")} isTime={isTimerComp} />
        </>
      )}

      {!data && <div style={{ display: "flex", justifyContent: "center", padding: 10 }}><div className="spin" /></div>}

      {/* v32 — opustenie: archív u mňa (primary) alebo odchod bez výsledkov */}
      {leaveAsk && (
        <ConfirmDelete title="Opustiť výzvu?" icon="logout"
          message={<>Ak výzvu archivuješ, odídeš z nej, ale <strong>výsledky ti ostanú v archíve</strong>. Pri opustení sa tvoje výsledky u teba neuložia.</>}
          archiveLabel="Archivovať u mňa" onArchive={async () => { setLeaveAsk(false); await archiveMe(); }}
          deleteLabel="Opustiť" onDelete={async () => { setLeaveAsk(false); await leave(); }}
          onClose={() => setLeaveAsk(false)} />
      )}
      {/* v32 — zmazanie tvorcom: archív pre všetkých (primary), alebo zmazať u mňa —
          ostatným účastníkom sa výzva automaticky archivuje s výsledkami */}
      {delAsk && (
        <ConfirmDelete title="Zmazať výzvu?" icon="trash"
          message={<>Archivovaním sa výzva ukončí k dnešku a <strong>všetkým ostane v archíve</strong>. Zmazaním zmizne u teba — ostatným účastníkom sa <strong>automaticky archivuje</strong> s výsledkami.</>}
          archiveLabel="Archivovať pre všetkých" onArchive={async () => { setDelAsk(false); await archiveAll(); }}
          deleteLabel="Zmazať u mňa" onDelete={async () => { setDelAsk(false); await del(); }}
          onClose={() => setDelAsk(false)} />
      )}

      {/* v32 — sticky footer v tele sheetu: zápis dnešného výsledku + Uložiť, pod tým časovač.
          Rebríček + grafy scrollujú nad ním (trieda .bz-sheet-stickyfoot v primitives.css).
          Lišta je spoločná pre obe záložky — zapisovať sa má dať odkiaľkoľvek. */}
      {data && (comp.is_creator || !closed) && (
      <div className="bz-sheet-stickyfoot">
        {!closed && (<>
        {/* dopisovanie za MINULÉ dni: predvolene sa píše dnešok a kalendár je schovaný za drobným
            odkazom „Pridať za minulý deň" (majiteľka 2026-09-05: pole „Deň" zaberalo pol lišty).
            Po ťuknutí sa ukáže výber dňa ohraničený behom súťaže, × vráti dnešok. Server hranice
            (nie do budúcnosti, nie pred štartom, nie po konci) kontroluje znova. */}
        {past && (
          <div className="bz-comp-past">
            <DateField value={day} onChange={setDay} label="Deň zápisu"
              min={data.start_day ? data.start_day : undefined}
              max={data.end_day && data.end_day < todayKey() ? data.end_day : todayKey()} />
            <IconButton variant="ghost" aria-label="Späť na dnešok" title="Späť na dnešok"
              onClick={() => { setPast(false); setDay(todayKey()); }}><Icon name="close" size={16} color="currentColor" /></IconButton>
          </div>
        )}
        {/* v2 — multi_day: „Pridať" sčítava záznamy; „Opraviť súčet" prepíše súčet dňa */}
        {multiDay && (
          <>
            <form onSubmit={(e) => saveResult(e, !fixToday)}>
              {labelRow(<>{fixToday ? "Opraviť súčet" : "Pridať záznam"}{isToday ? "" : ` (${dayName})`} · {isTimerComp ? "čas" : comp.metric_label}</>)}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {isTimerComp
                  ? <Input style={{ flex: 1, minWidth: 0 }} type="text" inputMode="text"
                      value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder="napr. 4:35 alebo 1:23:45" />
                  : <Input style={{ flex: 1, minWidth: 0 }} type="number" step="any"
                      value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder={comp.metric_label} />}
                <Button variant="primary" type="submit" disabled={busy}>{busy ? "…" : (fixToday ? "Uložiť" : "Pridať")}</Button>
                {fixToday && <Button variant="ghost" type="button" onClick={() => { setFixToday(false); setResult(""); }}>Zrušiť</Button>}
              </div>
            </form>
            {dayVal != null && !fixToday && (
              <div style={st.resultEntered}>
                <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="check" size={13} color="var(--accent)" /> {dayLabel}: <strong>{isTimerComp ? fmtDur(dayVal) : dayVal + " " + comp.metric_label}</strong>{me && me.result != null ? <span style={st.taskDesc}> · spolu {fmtVal(me.result)}</span> : null}</span>
                <button type="button" onClick={() => { setFixToday(true); setResult(isTimerComp ? fmtDur(dayVal) : String(dayVal)); }} style={st.linkBtn}>Opraviť {dayName}</button>
              </div>
            )}
          </>
        )}
        {!multiDay && (editToday ? (
          <form onSubmit={saveResult}>
            {labelRow(<>Tvoj výsledok{isToday ? "" : ` (${dayName})`} · {isTimerComp ? "čas" : comp.metric_label}</>)}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {isTimerComp
                ? <Input style={{ flex: 1, minWidth: 0 }} type="text" inputMode="text"
                    value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder="napr. 4:35 alebo 1:23:45" />
                : <Input style={{ flex: 1, minWidth: 0 }} type="number" step="any"
                    value={result} onChange={(e) => { setResult(e.target.value); setSaved(false); }} placeholder={comp.metric_label} />}
              <Button variant="primary" type="submit" disabled={busy}>{busy ? "…" : "Uložiť"}</Button>
              {dayVal != null && <Button variant="ghost" type="button" onClick={() => { setEditToday(false); setResult(isTimerComp ? fmtDur(dayVal) : String(dayVal)); }}>Zrušiť</Button>}
            </div>
          </form>
        ) : (
          <div style={st.resultEntered}>
            <span style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name="check" size={13} color="var(--accent)" /> {dayLabel}: <strong>{isTimerComp ? fmtDur(dayVal) : dayVal + " " + comp.metric_label}</strong>{me && me.result != null ? <span style={st.taskDesc}> · spolu {fmtVal(me.result)}</span> : null}</span>
            <span style={{ display: "inline-flex", gap: 12 }}>
              {!past && <button type="button" className="bz-comp-pastlink" onClick={startPast}>Pridať za minulý deň</button>}
              <button type="button" onClick={() => { setEditToday(true); }} style={st.linkBtn}>Upraviť {dayName}</button>
            </span>
          </div>
        ))}
        {saved && <div style={{ color: "var(--success)", fontSize: 13, fontWeight: 700 }}><Chk color="var(--success)" />Uložené</div>}
        {err && <div style={st.err}>{err}</div>}
        {/* v2 — stopky merajú výsledok: stop → auto-uloženie (pripočíta sa k vybranému dňu).
            Pri MINULOM dni sa nemá čo merať — ten sa dopisuje ručne do poľa vyššie.
            v32.1 — pri súťaži s JEDNÝM výsledkom na deň sa stopky skryjú, len čo je dnešok
            zapísaný. Uloženie tam totiž hodnotu PREPÍŠE (`add: multiDay` = false), takže
            druhé, kratšie meranie ticho zmazalo ten lepší výsledok aj body v rebríčku.
            Ručné pole sa proti tomu chráni už dávno — po zápise sa zamkne a prepísať sa dá
            až cez „Upraviť". Stopky idú teraz tým istým `editToday`, takže obe cesty
            vyžadujú rovnaké vedomé ťuknutie. Pri `multi_day` sa merania sčítavajú, tam
            sa nemá čo stratiť a stopky ostávajú stále dostupné. */}
        {isTimerComp && isToday && (multiDay || editToday) && (
          <Stopwatch id={"comp" + comp.id} onStop={stopwatchSave} />
        )}
        {/* časovač na výkon: primárne tlačidlo „Spustiť časovač" ROVNAKO ako v detaile návyku
            (variant="button"); po spustení sa prepne na sekundárne. */}
        {!isTimerComp && (comp.timer_sec || comp.timer_min) > 0 && (
          <Timer variant="button" id={"comp" + comp.id} seconds={comp.timer_sec || (comp.timer_min || 0) * 60} />
        )}
        </>)}
        {/* v33h — tvorcove akcie (Upraviť · Vyhodnotiť · kôš) ukotvené v sticky footeri, aj po vyhodnotení */}
        {comp.is_creator && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", ...(!closed ? { marginTop: 10, borderTop: "1px solid var(--neutral-border)", paddingTop: 10 } : {}) }}>
            <Button variant="ghost" block onClick={() => setEditing(true)}><Icon name="edit" size={16} color="currentColor" />Upraviť</Button>
            <Button variant="secondary" block onClick={toggleClose}>{closed ? <><Icon name="refresh" size={16} color="currentColor" />Znovu otvoriť</> : <><Icon name="check" size={16} color="currentColor" stroke={2.6} />Vyhodnotiť</>}</Button>
            <IconButton variant="ghost" aria-label="Zmazať výzvu" title="Zmazať výzvu" onClick={() => setDelAsk(true)} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
          </div>
        )}
        {/* Oficiálnu (globálnu) súťaž vyhodnocuje admin — jej zakladateľom je systémový účet,
            takže bez tohto by víťaz nikdy nedostal body. Upraviť/Zmazať ostávajú zakladateľovi. */}
        {!comp.is_creator && comp.can_eval && (
          <div style={{ marginTop: 10, borderTop: "1px solid var(--neutral-border)", paddingTop: 10 }}>
            <Button variant="secondary" block onClick={toggleClose}>{closed ? <><Icon name="refresh" size={16} color="currentColor" />Znovu otvoriť</> : <><Icon name="check" size={16} color="currentColor" stroke={2.6} />Vyhodnotiť</>}</Button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
